/**
 * engineNormal_Merge_Placeholder.ts - Placeholder processing methods for EngineNormal
 * Port of C# EngineNormal_Merge_Placeholder.cs
 * Pure ES Module version
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { CommonUtil } from '../../common/commonUtil.js';
import { getRecursionDepthError, msToNanos } from './engineNormal_Helper.js';
import type { EngineNormal } from './engineNormal.js';
import type { JsonObject } from '../../app/json/jsonValue.js';
import { JsonValue } from '../../app/json/jsonValue.js';
import type { TemplateOperation } from '../../model/templateOperation.js';
import { TemplateOperationKind } from '../../model/templateOperationKind.js';

type GetTemplateWithJsonFn = (appSite: string, currentTemplateName: string, placeholderName: string, appView: string | null, flowStack: string[] | null, configJson: JsonObject | null) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null };
type MergeJsonIntoTemplateFn = (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, operations: TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null) => string;

/**
 * Helper method to process simple placeholders only (without slotted template processing)
 */
export function replaceTemplatePlaceholders(engine: EngineNormal, html: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJson: GetTemplateWithJsonFn, mergeJsonIntoTemplate: MergeJsonIntoTemplateFn, configJson: JsonObject | null = null): string {
    const recursionDepthError = getRecursionDepthError(engine, depth, templateKey);
    if (recursionDepthError !== null) {
        return recursionDepthError;
    }

    let result = html;
    let searchPos = 0;

    while (searchPos < result.length) {
        // Look for opening placeholder {{
        const openStart = result.indexOf('{{', searchPos);
        if (openStart === -1) break;

        // Make sure it's not a slotted template ({{#) or special placeholder ({{@, {{$, {{/)
        // Note: {{! is allowed as it's a skip marker for placeholders, not slotted templates ({{!#)
        if (openStart + 2 < result.length && (result[openStart + 2] === '#' || result[openStart + 2] === '@' || result[openStart + 2] === '$' || result[openStart + 2] === '/')) {
            searchPos = openStart + 2;
            continue;
        }

        // Find closing }}
        const closeStart = result.indexOf('}}', openStart + 2);
        if (closeStart === -1) break;

        // Extract placeholder name (may start with ! for skip marker)
        const placeholderName = result.substring(openStart + 2, closeStart).trim();
        if (!placeholderName || !CommonUtil.isAlphaNumeric(placeholderName)) {
            searchPos = openStart + 2;
            continue;
        }

        // Create nested operations list BEFORE loading template (for JSON key tracking)
        const nestedOperations: TemplateOperation[] = [];
        const placeholderTimerStart = engine._genOperationJson ? performance.now() : 0;

        // Get clean placeholder name (without ! marker)
        const cleanPlaceholderName = placeholderName.startsWith('!') ? placeholderName.substring(1) : placeholderName;

        // Push onto flow stack for inheritance resolution (pop after recursive processing)
        flowStack.push(cleanPlaceholderName);

        Logger.flow(`ReplaceTemplatePlaceholders: placeholder=${placeholderName} depth=${depth}`, "Normal.TemplateRecursion");
        // Load template with JSON on-demand - pass currentTemplateName for component reuse pattern detection
        // GetTemplate will handle skip marker (!) if present
        let { html: templateContent, json: templateJson, resolvedTemplateName, resolvedAppSite: resolvedComponentAppSite } = getTemplateWithJson(appSite, currentTemplateName, placeholderName, appView, flowStack, configJson);
        Logger.flow(`ReplaceTemplatePlaceholders: placeholder=${placeholderName} resolved=${resolvedTemplateName == null ? 'null' : resolvedTemplateName} resolvedSize=${templateContent == null ? 0 : templateContent.length}`, "Normal.TemplateRecursion");

        // Update flow stack top with resolved template name for correct inheritance lookup
        if (resolvedTemplateName && resolvedTemplateName !== cleanPlaceholderName && flowStack.length > 0) {
            flowStack[flowStack.length - 1] = resolvedTemplateName;
        }

        // Check if template was found (templateContent can be empty for skip markers)
        if (templateContent !== null) {
            // Use resolved AppSite and template name for operations tracking (handles SearchAppSites and AppView fallback)
            const replacedWith = `${resolvedComponentAppSite}_${resolvedTemplateName || placeholderName}`;

            // Recursively process the loaded template - now placeholderName is the current template
            // Process BOTH slotted templates AND placeholders to track all nested operations
            // Skip processing if templateContent is empty (skip marker case)
            const nestedTemplateKey = replacedWith;
            // Check if this is a skip-marked or missing template
            const isPlaceholderSkipMarked = placeholderName.startsWith('!');
            const isSkippedOrMissing = resolvedTemplateName === engine._loader!.defaultSkipTemplateName || resolvedTemplateName === engine._loader!.defaultReplaceTemplateName;

            let processedReplacement: string;
            if (templateContent !== '' && !isPlaceholderSkipMarked && !isSkippedOrMissing) {
                Logger.flow(`ReplaceTemplatePlaceholders: branch=process placeholder=${placeholderName} viewHtmlSize=${templateContent.length}`, "Normal.TemplateRecursion");
                let modifiedTemplateContent = templateContent;

                // Merge JSON AFTER DynamicViewHandlers so {{$KeyName}} patterns get wrapped first
                modifiedTemplateContent = mergeJsonIntoTemplate(modifiedTemplateContent, templateJson, cleanPlaceholderName, resolvedTemplateName, resolvedComponentAppSite, nestedOperations, contextJson, configJson);
                Logger.flow(`ReplaceTemplatePlaceholders: postJsonMerge placeholder=${placeholderName} templateHtmlSize=${modifiedTemplateContent.length}`, "Normal.TemplateRecursion");

                // Add pre-order entry for placeholder component BEFORE processing (parent before children)
                // Keep ! prefix so skip components are identifiable (matches JSON engines)
                if (genComponentHtml) {
                    componentHtml.set(placeholderName, JsonValue.string(''));
                }

                processedReplacement = engine.mergeTemplateSlots(modifiedTemplateContent, appSite, placeholderName, appView, nestedTemplateKey, nestedOperations, depth + 1, componentHtml, genComponentHtml, contextJson, flowStack, getTemplateWithJson, mergeJsonIntoTemplate, replaceTemplatePlaceholders, configJson);
                Logger.flow(`ReplaceTemplatePlaceholders: postMergeTemplateSlots placeholder=${placeholderName} afterSlotsSize=${processedReplacement.length}`, "Normal.TemplateRecursion");
                processedReplacement = replaceTemplatePlaceholders(engine, processedReplacement, appSite, placeholderName, appView, nestedTemplateKey, nestedOperations, depth + 1, componentHtml, genComponentHtml, contextJson, flowStack, getTemplateWithJson, mergeJsonIntoTemplate, configJson);
                Logger.flow(`ReplaceTemplatePlaceholders: postRecursivePh placeholder=${placeholderName} processedReplacementSize=${processedReplacement.length}`, "Normal.TemplateRecursion");

            } else {
                processedReplacement = templateContent; // Empty string for skip markers
            }

            // Store processed template in componentHtml (update pre-order entry with actual content)
            // Keep ! prefix so skip components are identifiable (matches JSON engines)
            if (genComponentHtml) {
                componentHtml.set(placeholderName, JsonValue.string(processedReplacement));
            }

            // Replace the placeholder at the current position only
            result = result.substring(0, openStart) + processedReplacement + result.substring(closeStart + 2);

            // Determine operation type based on resolvedTemplateName
            let opType: TemplateOperationKind = TemplateOperationKind.ComponentReplace;
            if (resolvedTemplateName === engine._loader!.defaultSkipTemplateName) {
                opType = TemplateOperationKind.ComponentSkip;
            } else if (resolvedTemplateName === engine._loader!.defaultReplaceTemplateName) {
                opType = TemplateOperationKind.ComponentMissing;
            }

            // Add operation with nested operations
            operations.push({
                templateName: templateKey,
                kind: opType,
                target: '{{' + placeholderName + '}}',
                replacedWith: replacedWith,
                operations: nestedOperations
            } as TemplateOperation);

            searchPos = openStart + processedReplacement.length;
        } else {
            searchPos = closeStart + 2;
        }

        // Pop the flow stack entry we pushed at the start of this iteration
        flowStack.pop();

        if (engine._genOperationJson) {
            engine._placeholderProcessNanos += msToNanos(performance.now() - placeholderTimerStart);
        }
        engine._placeholderProcessCount++;
    }

    return result;
}
